import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Navigate } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import { isAdminEnabled } from '../config/adminAccess';
import { SHOP_RARITY_CHANCES } from '../features/scrabble/config/shopConfig';
import { RARITIES, RARITY_ORDER } from '../features/scrabble/config/rarityConfig';
import {
  isModifierEnabled,
  isShopKindEnabled,
  setModifierEnabled,
  setShopKindEnabled,
  SHOP_OPTION_MODIFIER_TYPES,
  TOGGLEABLE_SHOP_KINDS,
} from '../features/scrabble/config/shopOptions';
import {
  GAME_STATE_CHANGED_EVENT,
  getStoredGameState,
  writeStoredGameState,
} from '../features/scrabble/services/localStorageService';


const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const roundByLargestRemainder = (rawMap, totalTarget) => {
  const entries = Object.entries(rawMap);
  const floors = entries.map(([key, value]) => ({
    key,
    floor: Math.floor(value),
    remainder: value - Math.floor(value),
  }));

  const floorTotal = floors.reduce((sum, item) => sum + item.floor, 0);
  let deficit = totalTarget - floorTotal;

  floors.sort((a, b) => b.remainder - a.remainder);

  const result = floors.reduce((acc, item) => {
    acc[item.key] = item.floor;
    return acc;
  }, {});

  let index = 0;
  while (deficit > 0 && floors.length) {
    const key = floors[index % floors.length].key;
    result[key] += 1;
    deficit -= 1;
    index += 1;
  }

  return result;
};

const normalizeRarityDistribution = (input) => {
  const safe = RARITY_ORDER.reduce((acc, key) => {
    acc[key] = Math.max(0, Number(input?.[key]) || 0);
    return acc;
  }, {});

  const total = RARITY_ORDER.reduce((sum, key) => sum + safe[key], 0);
  if (total <= 0) {
    return { ...SHOP_RARITY_CHANCES };
  }

  const scaled = RARITY_ORDER.reduce((acc, key) => {
    acc[key] = (safe[key] / total) * 100;
    return acc;
  }, {});

  return roundByLargestRemainder(scaled, 100);
};

const rebalanceRarityDistribution = (current, editedKey, editedValue) => {
  const nextValue = clamp(Math.round(Number(editedValue) || 0), 0, 100);
  const otherKeys = RARITY_ORDER.filter((key) => key !== editedKey);
  const targetOtherTotal = 100 - nextValue;

  const currentOthers = otherKeys.reduce((acc, key) => {
    acc[key] = Math.max(0, Number(current?.[key]) || 0);
    return acc;
  }, {});

  const othersSum = otherKeys.reduce((sum, key) => sum + currentOthers[key], 0);

  let redistributedOthers;
  if (targetOtherTotal <= 0) {
    redistributedOthers = otherKeys.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
  } else if (othersSum <= 0) {
    const equalRaw = otherKeys.reduce((acc, key) => {
      acc[key] = targetOtherTotal / otherKeys.length;
      return acc;
    }, {});
    redistributedOthers = roundByLargestRemainder(equalRaw, targetOtherTotal);
  } else {
    const weightedRaw = otherKeys.reduce((acc, key) => {
      acc[key] = (currentOthers[key] / othersSum) * targetOtherTotal;
      return acc;
    }, {});
    redistributedOthers = roundByLargestRemainder(weightedRaw, targetOtherTotal);
  }

  return {
    ...redistributedOthers,
    [editedKey]: nextValue,
  };
};

const formatRarityLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

function AdminToolSection({ title, description, children }) {
  return (
    <Card
      sx={{
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        boxShadow: (theme) => `0 12px 30px ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>

          {description ? (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          ) : null}

          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}

function ShopRarityTool({
  hasActiveStoredRun,
  rarityDraft,
  total,
  feedback,
  onSliderChange,
  onSave,
}) {
  return (
    <>
      {!hasActiveStoredRun ? (
        <Alert severity="warning">
          No stored game state detected. Start a run first, then return here to edit in-state rarity chances.
        </Alert>
      ) : null}

      <Divider />

      <Stack spacing={2.1}>
        {RARITY_ORDER.map((key) => (
          <Box key={key}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
              <Typography sx={{ fontWeight: 700 }}>{formatRarityLabel(key)}</Typography>
              <Typography color="text.secondary">{rarityDraft[key]}%</Typography>
            </Stack>
            <Slider
              value={Number(rarityDraft[key]) || 0}
              min={0}
              max={100}
              step={1}
              disabled={!hasActiveStoredRun}
              onChange={(_, value) => onSliderChange(key, value)}
              valueLabelDisplay="auto"
            />
          </Box>
        ))}
      </Stack>

      <Divider />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontWeight: 700 }}>Total: {total}%</Typography>
        <Button variant="contained" onClick={onSave} disabled={!hasActiveStoredRun}>
          Save To Game State
        </Button>
      </Stack>

      {feedback ? <Alert severity={feedback.includes('saved') ? 'success' : 'info'}>{feedback}</Alert> : null}
    </>
  );
}

const MODIFIER_POOL_LABELS = {
  upgradeWeight: 'Upgrade',
  newTileWeight: 'New Tile',
  gameWeight: 'Game',
};

function ModifierRow({ modifier, enabled, onToggle }) {
  const rarity = RARITIES[modifier.rarity] || RARITIES.common;
  const pools = Object.entries(MODIFIER_POOL_LABELS)
    .filter(([key]) => modifier.shop[key] > 0)
    .map(([key, label]) => `${label}: ${modifier.shop[key]}`);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
        opacity: enabled ? 1 : 0.45,
        transition: 'opacity 0.15s',
      }}
    >
      <Box
        sx={{
          width: 4,
          alignSelf: 'stretch',
          minHeight: 40,
          borderRadius: 2,
          flexShrink: 0,
          backgroundColor: rarity.color,
          mt: 0.25,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" sx={{ mb: 0.3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
            {modifier.label || modifier.id}
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              fontFamily: 'monospace',
              color: 'text.disabled',
              letterSpacing: 0.2,
            }}
          >
            {modifier.id}
          </Typography>
          <Chip
            size="small"
            label={rarity.label}
            sx={{
              height: 17,
              fontSize: 9.5,
              fontWeight: 700,
              borderRadius: 0.6,
              backgroundColor: alpha(rarity.color, 0.14),
              color: rarity.color,
            }}
          />
        </Stack>

        {modifier.description ? (
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.4 }}>
            {modifier.description}
          </Typography>
        ) : null}

        {pools.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {pools.map((p) => (
              <Typography
                key={p}
                sx={{
                  fontSize: 10,
                  color: 'text.disabled',
                  backgroundColor: (theme) => alpha(theme.palette.divider, 0.5),
                  borderRadius: 0.5,
                  px: 0.6,
                  py: 0.15,
                }}
              >
                {p}
              </Typography>
            ))}
          </Stack>
        ) : null}
      </Box>

      <Switch
        size="small"
        checked={enabled}
        onChange={() => onToggle(modifier.id)}
        sx={{ flexShrink: 0, mt: 0.25 }}
      />
    </Box>
  );
}

function ModifierToggleWidget() {
  const [enabledState, setEnabledState] = React.useState(() =>
    Object.fromEntries(SHOP_OPTION_MODIFIER_TYPES.map((m) => [m.id, isModifierEnabled(m.id)]))
  );

  const handleToggle = React.useCallback((id) => {
    setEnabledState((prev) => {
      const next = !prev[id];
      setModifierEnabled(id, next);
      return { ...prev, [id]: next };
    });
  }, []);

  const handleEnableAll = React.useCallback(() => {
    const next = Object.fromEntries(SHOP_OPTION_MODIFIER_TYPES.map((m) => [m.id, true]));
    Object.keys(next).forEach((id) => setModifierEnabled(id, true));
    setEnabledState(next);
  }, []);

  const handleDisableAll = React.useCallback(() => {
    const next = Object.fromEntries(SHOP_OPTION_MODIFIER_TYPES.map((m) => [m.id, false]));
    Object.keys(next).forEach((id) => setModifierEnabled(id, false));
    setEnabledState(next);
  }, []);

  const tileModifiers = SHOP_OPTION_MODIFIER_TYPES.filter((m) => m.category === 'tile');
  const gameModifiers = SHOP_OPTION_MODIFIER_TYPES.filter((m) => m.category === 'game');
  const disabledCount = Object.values(enabledState).filter((v) => !v).length;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {disabledCount > 0 ? `${disabledCount} modifier${disabledCount === 1 ? '' : 's'} disabled` : 'All modifiers enabled'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={handleEnableAll}>
            Enable All
          </Button>
          <Button size="small" color="error" onClick={handleDisableAll}>
            Disable All
          </Button>
        </Stack>
      </Stack>

      <Divider />
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Tile Modifiers
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 2 }}>
        {tileModifiers.map((m) => (
          <Box key={m.id} sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <ModifierRow modifier={m} enabled={enabledState[m.id] ?? true} onToggle={handleToggle} />
          </Box>
        ))}
      </Box>

      <Divider />
      <Typography sx={{ fontWeight: 700, fontSize: 13, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Game Modifiers
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 2 }}>
        {gameModifiers.map((m) => (
          <Box key={m.id} sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
            <ModifierRow modifier={m} enabled={enabledState[m.id] ?? true} onToggle={handleToggle} />
          </Box>
        ))}
      </Box>
    </>
  );
}

function ShopKindToggleWidget() {
  const [enabledState, setEnabledState] = React.useState(() =>
    Object.fromEntries(TOGGLEABLE_SHOP_KINDS.map((k) => [k.id, isShopKindEnabled(k.id)]))
  );

  const handleToggle = React.useCallback((id) => {
    setEnabledState((prev) => {
      const next = !prev[id];
      setShopKindEnabled(id, next);
      return { ...prev, [id]: next };
    });
  }, []);

  return (
    <Stack divider={<Divider />}>
      {TOGGLEABLE_SHOP_KINDS.map((kind) => {
        const enabled = enabledState[kind.id] ?? true;
        return (
          <Box
            key={kind.id}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              py: 1,
              opacity: enabled ? 1 : 0.45,
              transition: 'opacity 0.15s',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{kind.label}</Typography>
                <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'text.disabled', letterSpacing: 0.2 }}>
                  {kind.id}
                </Typography>
              </Stack>
              {kind.description ? (
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{kind.description}</Typography>
              ) : null}
            </Box>
            <Switch
              size="small"
              checked={enabled}
              onChange={() => handleToggle(kind.id)}
              sx={{ flexShrink: 0, mt: 0.25 }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

function AdminPage() {
  const adminEnabled = isAdminEnabled();
  const [storedGameState, setStoredGameState] = React.useState(() => getStoredGameState());
  const [rarityDraft, setRarityDraft] = React.useState(() =>
    normalizeRarityDistribution(getStoredGameState()?.runSettings?.shop?.rarityChances || SHOP_RARITY_CHANCES)
  );
  const [feedback, setFeedback] = React.useState('');

  const hasActiveStoredRun = Boolean(storedGameState && typeof storedGameState === 'object');

  React.useEffect(() => {
    const syncFromStorage = () => {
      const latestGameState = getStoredGameState();
      setStoredGameState(latestGameState);
      setRarityDraft(
        normalizeRarityDistribution(
          latestGameState?.runSettings?.shop?.rarityChances || SHOP_RARITY_CHANCES
        )
      );
    };

    syncFromStorage();

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener(GAME_STATE_CHANGED_EVENT, syncFromStorage);
    window.addEventListener('storage', syncFromStorage);

    return () => {
      window.removeEventListener(GAME_STATE_CHANGED_EVENT, syncFromStorage);
      window.removeEventListener('storage', syncFromStorage);
    };
  }, []);

  const total = React.useMemo(
    () => RARITY_ORDER.reduce((sum, key) => sum + (Number(rarityDraft[key]) || 0), 0),
    [rarityDraft]
  );

  const handleSliderChange = React.useCallback((rarityKey, nextValue) => {
    setRarityDraft((prev) => rebalanceRarityDistribution(prev, rarityKey, nextValue));
    setFeedback('');
  }, []);

  const handleSave = React.useCallback(() => {
    const current = getStoredGameState();
    if (!current || typeof current !== 'object') {
      setFeedback('No active stored run found. Start or resume a run first.');
      return;
    }

    const normalizedDraft = normalizeRarityDistribution(rarityDraft);

    const nextState = {
      ...current,
      runSettings: {
        ...(current.runSettings || {}),
        shop: {
          ...((current.runSettings && current.runSettings.shop) || {}),
          rarityChances: normalizedDraft,
        },
      },
    };

    writeStoredGameState(nextState);
    setStoredGameState(nextState);
    setRarityDraft(normalizedDraft);
    setFeedback('Shop rarity chances saved to game state.');
  }, [rarityDraft]);

  const adminTools = [
    {
      id: 'shop-rarity',
      title: 'Shop Rarity Chances',
      description: 'Adjust sliders for testing. Values always rebalance to total 100%.',
      render: () => (
        <ShopRarityTool
          hasActiveStoredRun={hasActiveStoredRun}
          rarityDraft={rarityDraft}
          total={total}
          feedback={feedback}
          onSliderChange={handleSliderChange}
          onSave={handleSave}
        />
      ),
    },
    {
      id: 'modifier-toggles',
      title: 'Modifier Enable / Disable',
      description: 'Toggle modifiers on or off. Disabled modifiers are excluded from all shop pools. Changes take effect at the next shop phase. Settings persist across refreshes.',
      render: () => <ModifierToggleWidget />,
    },
    {
      id: 'shop-kind-toggles',
      title: 'Shop Option Types',
      description: 'Enable or disable specific shop offer types. Changes take effect at the next shop phase.',
      render: () => <ShopKindToggleWidget />,
    },
  ];

  if (!adminEnabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, sm: 3 },
        py: { xs: 3, md: 5 },
        background: 'linear-gradient(180deg, #f8fbff 0%, #f5f7fb 100%)',
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: 940, mx: 'auto' }}>
        <PageTitle subtitle="Admin Tools" />

        {adminTools.map((tool) => (
          <AdminToolSection key={tool.id} title={tool.title} description={tool.description}>
            {tool.render()}
          </AdminToolSection>
        ))}
      </Stack>
    </Box>
  );
}

export default AdminPage;
